import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator, Alert, TextInput } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import ScreenBackground from '../components/ScreenBackground';
import { storageService } from '../services/storageService';
import { firestoreService } from '../services/firestoreService';
import { getCurrentBridgeUser } from '../services/authBridge';
import { KYC_WALL_ENFORCED } from '../constants/featureFlags';
import { aplazarVerificacionEnEstaSesion, rutaSegunRol } from './_layout';
import { ayudaPlaca, normalizarPlaca, revisarPlaca } from '../utils/placa';
import { entrarALaApp } from '../utils/volver';
import { leerKycDocs } from '../utils/kycDocs';
import { mensajeDeError } from '../utils/mensajeDeError';
// EL `SafeAreaView` DE REACT-NATIVE NO HACE NADA EN ANDROID.
//
// Esta pantalla lo usaba, y su propia documentacion lo dice: «available only on
// iOS». En Android se dibuja como un `<View>` pelado, asi que el relleno de
// arriba era solo el `paddingTop: 16` de `stickyHeader` — menos de la mitad de
// una barra de estado normal (24-48 px, mas en un telefono con muesca). Los
// botones «Cambiar vehiculo» y «Ahora no» quedaban DEBAJO de la hora y la
// bateria. Es la deformacion que el fundador fotografio el 30-sep-2026.
//
// `PantallaSegura` lee los margenes de verdad con `useSafeAreaInsets()`. No
// hace falta montar ningun proveedor: expo-router ya monta
// `SafeAreaProviderCompat` dentro de su Stack.
import PantallaSegura from '../components/PantallaSegura';

// TODOS los títulos de documento que esta pantalla puede pedir, en cualquiera
// de sus variantes (pasajero, carro, motor, escolar). Existe para poder
// RECONOCER lo que el usuario ya subió: en Firestore los documentos viven en
// `kycDocs` con la clave "aplanada" (`slugify(titulo)`), así que sin esta lista
// no hay forma de volver de la clave al título que se pinta en pantalla.
// Al añadir un DocumentUploadRow nuevo, añade aquí su título o no se rehidrata.
/**
 * Qué datos del vehículo NO están guardados todavía, con el nombre con el que
 * se le habla al chofer (no el nombre técnico del campo).
 *
 * POR QUÉ EXISTE: se comprobó contra la base de datos real que hay choferes
 * VERIFICADOS y en línea sin `vehicleBrand`, `vehicleModel`, `vehicleColor` ni
 * `licensePlate` — los campos ni siquiera existen en su documento. El único
 * sitio que los escribía era el botón "Validar Documentos y Continuar" de esta
 * misma pantalla, o sea un paso del alta por el que ya pasaron (o por el que
 * pasaron ANTES de que ese bloque existiera). Resultado: el pasajero no ve qué
 * carro esperar y nadie se lo dice al chofer.
 *
 * El AÑO no entra en la lista a propósito: es opcional en el formulario.
 */
const faltantesDeVehiculo = (v: {
  brand?: string | null;
  model?: string | null;
  color?: string | null;
  plate?: string | null;
}): string[] => {
  const faltan: string[] = [];
  if (!String(v.brand ?? '').trim()) faltan.push('la marca');
  if (!String(v.model ?? '').trim()) faltan.push('el modelo');
  if (!String(v.color ?? '').trim()) faltan.push('el color');
  if (!String(v.plate ?? '').trim()) faltan.push('la placa');
  return faltan;
};

const TITULOS_DE_DOCUMENTO: string[] = [
  'Cédula - Frente',
  'Cédula - Dorso',
  'Licencia de Conducir',
  'Papel de Buena Conducta',
  'Matrícula del Vehículo',
  'Seguro Vigente',
  '1. Lateral Derecho Completo',
  '2. Lado de la Placa',
  'Casco rotulado con tu placa',
  '3. Modelo del Chasis',
  '1. Frente del Vehículo',
  '2. Lateral Derecho',
  '3. Lateral Izquierdo',
  '4. Parte Trasera',
  '5. Interior del Vehículo',
  '5. Foto de Asientos y Cinturones',
  '6. Seguro de Puertas Niños',
];

export default function KycDocumentsScreen() {
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  // Tomamos el uid PREFIRIENDO el bridge (que cubre native + REST). El store
  // userFirebaseUid solo se setea cuando el observer nativo se dispara, y si
  // el signup cayó al fallback REST ese observer nunca corre → bridge tiene
  // el uid, store lo tiene null. Si solo miráramos el store, este código
  // caería al branch "sin sesión" y marcaría falsamente como cargado.
  const storeUid = useAppStore((state) => state.userFirebaseUid);
  const userFirebaseUid = getCurrentBridgeUser()?.uid ?? storeUid;
  const setDriverVerified = useAppStore((state) => state.setDriverVerified);
  // `isDriverVerified` lo escribe el SERVIDOR al aprobar el KYC y _layout lo
  // baja al almacén. Aquí NO decide nada del embudo: solo sirve para reconocer
  // al chofer que YA pasó la verificación y vuelve únicamente a completar o
  // corregir los datos de su vehículo, para darle un botón de guardar que no
  // lo obligue a repetir la biometría.
  const isDriverVerified = useAppStore((state) => state.isDriverVerified);
  const isVerified = useAppStore((state) => state.isVerified);
  // `focoVehiculo=1` lo manda el aviso de "Mi Suscripción": abre esta pantalla
  // ya desplazada al formulario del vehículo, que es lo único que va a tocar.
  const { vehicle, focoVehiculo } = useLocalSearchParams<{ vehicle: string; focoVehiculo?: string }>();

  const isMotorcycle = vehicle === 'MOTORCYCLE';
  // "No conduce" = no se le piden papeles de vehículo.
  // FOOD_DELIVERY es el CLIENTE que pide comida; el repartidor es
  // DELIVERY_DRIVER. Antes esta línea solo miraba PASSENGER, así que al
  // cliente de comida —que /kyc-welcome manda aquí igual que al pasajero— se
  // le exigían licencia de conducir, matrícula, seguro, PLACA y cinco fotos
  // de un vehículo que no tiene. Sin placa no dejaba continuar: registro
  // imposible de terminar.
  const isPassenger = role === 'PASSENGER' || role === 'FOOD_DELIVERY';

  // ══════════════════════════════════════════════════════════════════════
  // CONDUCE, PERO EL CARRO NO ES SUYO
  // ══════════════════════════════════════════════════════════════════════
  //
  // `CAR_RENTER` es el chofer que busca un carro alquilado: entra por el
  // Marketplace o por el enlace mágico de un dueño de rent car. Conduce, así
  // que SÍ necesita licencia — pero no tiene matrícula, ni seguro, ni placa,
  // ni fotos de un vehículo, porque el vehículo es del dueño.
  //
  // Esta pantalla solo tenía UN interruptor (`isPassenger`) para dos preguntas
  // distintas: «¿conduce?» y «¿el carro es suyo?». Como CAR_RENTER no estaba en
  // ninguna lista, caía del lado de los choferes con carro propio y se le
  // exigían marca, modelo, color y PLACA. Y sin placa `validarVehiculo` no deja
  // continuar: el registro era imposible de terminar.
  //
  // Es EXACTAMENTE el mismo fallo que ya se arregló para el cliente de comida
  // —está contado tres líneas más arriba— y volvió a pasar por el mismo
  // motivo: una pregunta nueva metida en una bandera vieja. Por eso ahora son
  // dos banderas y no una lista más larga.
  //
  // Lo que SÍ se le sigue pidiendo: cédula (las dos caras), licencia de
  // conducir y la selfie en vivo. Los papeles del carro los pone el dueño al
  // publicarlo, y el dueño además revisa a mano los documentos del candidato
  // antes de entregarle las llaves (functions/src/drivers.ts).
  const sinVehiculoPropio = role === 'CAR_RENTER';
  /** Conduce con carro propio: es a quien se le piden los papeles del carro. */
  const conVehiculoPropio = !isPassenger && !sinVehiculoPropio;
  // Quien transporta estudiantes: el ÚNICO a quien se le sigue pidiendo el
  // Papel de Buena Conducta (27-sep-2026).
  const esChoferEscolar = role === 'SCHOOL_DRIVER';

  const setKycDocumentUri = useAppStore((state) => state.setKycDocumentUri);
  const setKycDocumentBase64 = useAppStore((state) => state.setKycDocumentBase64);
  const kycDocumentBase64 = useAppStore((state) => state.kycDocumentBase64);
  const setVehicleInfo = useAppStore((state) => state.setVehicleInfo);
  const existingBrand = useAppStore((state) => state.vehicleBrand);
  const existingModel = useAppStore((state) => state.vehicleModel);
  const existingColor = useAppStore((state) => state.vehicleColor);
  const existingYear = useAppStore((state) => state.vehicleYear);
  const existingPlate = useAppStore((state) => state.licensePlate);
  // Ahora guardamos la URL pública (Storage) en lugar de un boolean, así
  // todos los documentos quedan persistidos y referenciables desde el perfil.
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, string>>({});
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  // Datos del vehículo (Uber-style). Pre-cargamos desde el store por si el
  // user vuelve a esta pantalla — así no tiene que re-teclear.
  const [vehicleBrand, setVehicleBrandLocal] = useState<string>(existingBrand ?? '');
  const [vehicleModel, setVehicleModelLocal] = useState<string>(existingModel ?? '');
  const [vehicleColor, setVehicleColorLocal] = useState<string>(existingColor ?? '');
  const [vehicleYear, setVehicleYearLocal] = useState<string>(existingYear ?? '');
  const [licensePlate, setLicensePlateLocal] = useState<string>(existingPlate ?? '');

  // ══════════════════════════════════════════════════════════════════════
  // EL CAMINO QUE FALTABA: GUARDAR SOLO EL VEHÍCULO.
  //
  // El chofer ya verificado que entra aquí a poner marca/modelo/color/placa
  // no tenía por dónde: el único botón que las guardaba es el del final, y
  // ese sigue hacia la biometría — en móvil ni siquiera le deja pasar,
  // porque el base64 del frente de la cédula solo existe si la foto se tomó
  // en ESTA sesión. Se quedaba con el dato escrito en pantalla y sin guardar.
  //
  // Esto NO toca el embudo de alta: durante el registro no se pinta nada de
  // esto (ver `mostrarGuardadoDeVehiculo`), para no poner un segundo botón
  // compitiendo con "Validar Documentos y Continuar", que funciona.
  // ══════════════════════════════════════════════════════════════════════
  const [guardandoVehiculo, setGuardandoVehiculo] = useState(false);
  // El resultado también se pinta EN LA PANTALLA, no solo en un Alert: en web
  // `Alert.alert` de react-native no muestra nada, y sin esto el chofer que
  // guarda desde el navegador no sabría si quedó o no.
  const [resultadoGuardado, setResultadoGuardado] = useState<{ ok: boolean; texto: string } | null>(null);
  // Qué falta SEGÚN LO GUARDADO, no según lo que hay escrito en las cajas:
  // mientras no se pulse Guardar, para el pasajero ese dato no existe.
  const [faltantesVehiculo, setFaltantesVehiculo] = useState<string[]>(() =>
    faltantesDeVehiculo({
      brand: existingBrand,
      model: existingModel,
      color: existingColor,
      plate: existingPlate,
    })
  );
  // Solo se muestra al chofer que YA está verificado (o al que llega desde el
  // aviso). Para el que está haciendo el alta, la pantalla queda idéntica.
  const mostrarGuardadoDeVehiculo = conVehiculoPropio && (isDriverVerified || focoVehiculo === '1');
  // Vino SOLO a completar su vehículo, no a darse de alta. Cambia a dónde
  // vuelve (ver `handleBackToVehiclePicker`).
  const vinoSoloPorElVehiculo = focoVehiculo === '1';
  // ══════════════════════════════════════════════════════════════════════
  // EL CHOFER YA VERIFICADO NO ESTA DANDOSE DE ALTA
  // ══════════════════════════════════════════════════════════════════════
  // `vinoSoloPorElVehiculo` mira SOLO el parametro `foco=vehiculo` de la URL, y
  // de los seis sitios que abren esta pantalla solo DOS lo pasan
  // (driver-subscription.tsx y kyc-welcome.tsx). Los otros cuatro la abren
  // pelada: profile.tsx:540 —por donde entro el fundador—, map.tsx:5704,
  // edit-profile.tsx:96 y school-driver-setup.tsx:98.
  //
  // Para un chofer YA APROBADO que entra desde su perfil a mirar sus
  // documentos, eso dejaba dos cosas mal:
  //
  //   · el boton de salir decia «Cambiar vehiculo», ofreciendole rehacer algo
  //     que ya tiene puesto en el perfil, y
  //   · al pulsarlo hacia `router.replace('/kyc-welcome')`, que es un paso del
  //     ALTA. Elegir ahi un vehiculo llama a `setDriverRole` y reescribe rol y
  //     tipo de vehiculo por Cloud Function. Y como se llega con `replace`, el
  //     historial se borra: no habia vuelta. Eso es lo que se veia como «la app
  //     se rompe al tratar de salir de esa pantalla».
  //
  // Un usuario verificado nunca esta a medio registrarse, asi que para el si es
  // seguro el `back()` que el embudo del alta evita a proposito (ver el
  // comentario de `handleBackToVehiclePicker`).
  // Se mira tambien `isVerified`: la verificacion de IDENTIDAD la tiene tanto
  // el chofer como el pasajero, y un pasajero aprobado que entre aqui desde su
  // perfil quedaba igual de atrapado que el chofer. `isDriverVerified` sigue
  // porque es el que marca al chofer habilitado.
  const yaEstaVerificado = isDriverVerified || isVerified;

  // Llevarlo al formulario, no solo abrirle la pantalla: esta vista empieza
  // con dos avisos y los documentos de identidad, y el chofer que viene del
  // aviso creería que el formulario no está. El efecto que lo usa vive más
  // abajo, porque depende de `rehidratando`.
  const scrollRef = useRef<ScrollView | null>(null);
  const yDelFormularioVehiculo = useRef<number | null>(null);
  const yaDesplazado = useRef(false);
  const desplazarAlVehiculo = () => {
    if (focoVehiculo !== '1' || yaDesplazado.current) return;
    const y = yDelFormularioVehiculo.current;
    if (y == null || !scrollRef.current) return;
    yaDesplazado.current = true;
    // −12 para que el borde superior de la tarjeta no quede pegado al techo.
    scrollRef.current.scrollTo({ y: Math.max(0, y - 12), animated: true });
  };

  /** Convierte un título tipo "1. Lateral Derecho Completo" a "1_lateral_derecho_completo" */
  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  // ══════════════════════════════════════════════════════════════════════
  // REHIDRATAR LO YA SUBIDO.
  //
  // `uploadedDocs` arrancaba SIEMPRE vacío. El usuario que subía seis fotos,
  // se quedaba sin batería o sin datos y volvía, encontraba la pantalla como
  // el primer día: todas las filas en "Cámara", y a repetir cédula, licencia,
  // matrícula, seguro y las cuatro (o cinco) fotos del vehículo. Con una
  // conexión dominicana normal eso es abandonar el registro.
  //
  // Las URLs ya están en Firestore (`kycDocs`, que escribe handleUpload aquí
  // mismo). Solo había que leerlas de vuelta.
  // ══════════════════════════════════════════════════════════════════════
  const [rehidratando, setRehidratando] = useState(true);
  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!userFirebaseUid) {
        if (vivo) setRehidratando(false);
        return;
      }
      try {
        const perfil: any = await firestoreService.getUserProfile(userFirebaseUid);
        if (!vivo) return;
        // ⚠️ `leerKycDocs` Y NO `perfil?.kycDocs` A SECAS.
        //
        // Era el ÚNICO lector de todo src/ que seguía leyendo el mapa crudo. Los
        // documentos viejos NO están en un mapa: están como campos sueltos de
        // primer nivel con punto en el nombre (`kycDocs.cedula_frente`,
        // `kycDocs.1_frente_del_vehiculo`…). Medido en Firestore el 11-sep-2026:
        // el documento del fundador tiene sus NUEVE documentos guardados así.
        //
        // Con el mapa crudo, `guardados` salía vacío y esta pantalla le decía que
        // no había subido nada. Y eso no era cosmético: dentro de este mismo
        // bloque se rehidrata `kycDocumentUri`, que es lo que mira
        // `kyc-biometrics` — sin él, el chofer que vuelve en una sesión nueva
        // queda TRANCADO en la biometría con un "Falta la cédula" sobre una
        // cédula que sí había subido.
        const guardados = leerKycDocs(perfil);
        if (guardados && typeof guardados === 'object') {
          const yaSubidos: Record<string, string> = {};
          for (const titulo of TITULOS_DE_DOCUMENTO) {
            const url = guardados[slugify(titulo)]?.url;
            if (typeof url === 'string' && url.length > 0) yaSubidos[titulo] = url;
          }
          // Lo tomado EN ESTA SESIÓN manda sobre lo que había guardado: si el
          // usuario acaba de repetir una foto mientras esto viajaba, no se la
          // pisamos con la vieja.
          setUploadedDocs((prev) => ({ ...yaSubidos, ...prev }));

          // El frente de la cédula es el que la biométrica compara con la
          // selfie. Si ya estaba subido, dejamos su URL en el store para que
          // el siguiente paso no diga "falta la cédula".
          const urlFrente = yaSubidos['Cédula - Frente'];
          if (urlFrente && !useAppStore.getState().kycDocumentUri) {
            setKycDocumentUri(urlFrente);
          }
        }

        // ── LOS DATOS DEL VEHÍCULO, DESDE FIRESTORE ─────────────────────────
        // El almacén local puede venir vacío (reinstalación, sesión nueva, o
        // sencillamente porque nunca se guardaron). La verdad de lo que ve el
        // pasajero está en `/users/{uid}`, así que es de ahí de donde se lee
        // qué falta — y no de lo que el almacén recuerde.
        //
        // Solo rellenamos las cajas VACÍAS: si el chofer ya estaba tecleando
        // mientras esta lectura viajaba, no se le pisa lo escrito.
        //
        // Y si no hay documento que leer (`perfil` nulo) no se toca nada: sin
        // dato no se puede afirmar que le falte nada, y el aviso quedaría
        // acusándolo de algo que no sabemos.
        if (!perfil) return;
        const marcaBD = String(perfil?.vehicleBrand ?? '').trim();
        const modeloBD = String(perfil?.vehicleModel ?? '').trim();
        const colorBD = String(perfil?.vehicleColor ?? '').trim();
        const anioBD = String(perfil?.vehicleYear ?? '').trim();
        const placaBD = String(perfil?.licensePlate ?? '').trim();
        if (marcaBD) setVehicleBrandLocal((prev) => prev.trim() || marcaBD);
        if (modeloBD) setVehicleModelLocal((prev) => prev.trim() || modeloBD);
        if (colorBD) setVehicleColorLocal((prev) => prev.trim() || colorBD);
        if (anioBD) setVehicleYearLocal((prev) => prev.trim() || anioBD);
        if (placaBD) setLicensePlateLocal((prev) => prev.trim() || placaBD);
        setFaltantesVehiculo(
          faltantesDeVehiculo({ brand: marcaBD, model: modeloBD, color: colorBD, plate: placaBD })
        );
      } catch (e: any) {
        // Que no se pueda leer no borra nada: en el peor caso el usuario ve la
        // pantalla como antes de este arreglo y vuelve a tomar las fotos.
        console.warn('[KYC] no se pudo recuperar lo ya subido:', e?.message);
      } finally {
        if (vivo) setRehidratando(false);
      }
    })();
    return () => { vivo = false; };
  }, [userFirebaseUid, setKycDocumentUri]);

  // Desplazar al formulario del vehículo cuando se llega desde el aviso.
  // `rehidratando` está en las dependencias porque el letrero "Buscando los
  // documentos que ya subiste…" desaparece al terminar y eso sube todo el
  // contenido: si se desplazara antes, la medida quedaría vieja.
  useEffect(() => {
    desplazarAlVehiculo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focoVehiculo, rehidratando]);

  const handleUpload = async (title: string) => {
    setUploadingDoc(title);
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert('Permiso denegado', 'Necesitamos acceso a la cámara para tomar las fotos de tus documentos en vivo.');
          setUploadingDoc(null);
          return;
        }
      }

      const launchMethod = Platform.OS === 'web' ? ImagePicker.launchImageLibraryAsync : ImagePicker.launchCameraAsync;
      const result = await launchMethod({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 0.8,
        // base64 nativo: evita pasar por FileReader/fetch+blob en RN+Hermes
        // (que lanza "Creating blobs from ArrayBuffer not supported").
        // ImagePicker lo devuelve directamente listo para enviar al backend.
        base64: true,
      });

      if (result.canceled || !result.assets?.[0]) {
        setUploadingDoc(null);
        return;
      }

      const localUri = result.assets[0].uri;
      const localBase64 = result.assets[0].base64 ?? null;

      // 1) Guardar URI local + base64 (solo para el Frente, que es la cara
      //    que el revisor compara con la selfie). El dorso solo va
      //    a Storage como evidencia.
      if (title === 'Cédula - Frente') {
        setKycDocumentUri(localUri);
        setKycDocumentBase64(localBase64);
      }

      // 2) Subir a Firebase Storage vía backend (Admin SDK).
      //    Si no hay uid o no hay base64 (web), NO marcamos como cargado —
      //    antes había un branch "sin sesión" que ponía el botón verde
      //    mintiendo, y el usuario veía "Documento Cargado" sin que
      //    nada se hubiera subido. Ese branch fue eliminado.
      if (!userFirebaseUid) {
        Alert.alert(
          'Sesión no detectada',
          'Tu sesión no se detectó. Cierra la app y vuelve a iniciar sesión antes de subir el documento.'
        );
        setUploadingDoc(null);
        return;
      }
      try {
        const documentType = slugify(title);
        const url = await storageService.uploadKYCDocument(
          userFirebaseUid,
          documentType,
          localUri,
          localBase64
        );
        setUploadedDocs((prev) => ({ ...prev, [title]: url }));

        // 3) Actualizar el perfil del usuario con la URL.
        //    Firestore puede colgarse silenciosamente en RN+Hermes (SDK web)
        //    — lo envolvemos en try/catch para no bloquear el flujo si falla.
        try {
          // ⚠️ MAPA ANIDADO, NO CLAVE CON PUNTO.
          // Antes esto mandaba `{ 'kycDocs.1_frente_del_vehiculo': {...} }`. En
          // Firestore el punto SOLO es una ruta dentro de `update()`; con
          // `set({merge:true})` — que es lo que hace updateUserProfile — el
          // punto es parte del NOMBRE del campo. Resultado: se creaban campos
          // sueltos llamados literalmente "kycDocs.1_frente_del_vehiculo" y el
          // mapa `kycDocs` NUNCA existía.
          //
          // Eso dejaba ciego a todo el que lee `kycDocs`: la tarjeta "Mi
          // vehículo" del perfil, y el chequeo del Papel de Buena Conducta que
          // decide si un chofer puede ponerse en línea (map.tsx) y el de
          // school-driver-setup.
          //
          // Con `merge: true` los mapas anidados se combinan campo a campo, así
          // que esto añade la foto sin borrar las demás.
          const profileUpdate: any = {
            kycDocs: {
              [documentType]: { url, uploadedAt: new Date().toISOString() },
            },
          };
          // PAPEL DE BUENA CONDUCTA — obligatorio para operar. Al subirlo queda
          // en revisión del admin, que verifica que NO registre antecedentes
          // judiciales y aprueba/rechaza. El gate de "ponerse online" bloquea al
          // chofer si queda REJECTED (o si no lo subió al terminar la prueba).
          if (/conducta/i.test(documentType)) {
            profileUpdate.conductPaperUrl = url;
            profileUpdate.conductPaperStatus = 'PENDING_REVIEW';
            profileUpdate.conductPaperUploadedAt = new Date().toISOString();
          }
          await firestoreService.updateUserProfile(userFirebaseUid, profileUpdate);
        } catch (firestoreErr) {
          console.warn('[KYC] updateUserProfile falló (no crítico):', firestoreErr);
        }
      } catch (e: any) {
        console.warn('Storage upload falló:', e?.code, e?.status, e?.message);
        // Mensaje granular por tipo de falla. Antes todo caía al genérico
        // "Hubo un problema subiendo el documento" que confundía al user
        // cuando el backend respondía 429 por rate-limit — parecía bug de
        // cámara o de red cuando en realidad solo había que esperar.
        const rawMsg = String(e?.message ?? '').toLowerCase();
        const isRateLimit =
          e?.status === 429 || rawMsg.includes('demasiad') || rawMsg.includes('rate');
        const isNetwork = rawMsg.includes('network') || rawMsg.includes('failed to fetch');

        let body: string;
        if (isRateLimit) {
          body =
            'El servidor recibió muchas subidas seguidas desde tu red. Espera unos minutos y vuelve a intentar — tus documentos ya cargados se mantienen.';
        } else if (isNetwork) {
          body = 'Sin conexión estable. Revisa tu Wi-Fi/datos y vuelve a tomar la foto.';
        } else {
          body = mensajeDeError(e, 'Hubo un problema subiendo el documento. Vuelve a tomar la foto.');
        }
        Alert.alert('No se pudo subir el documento', body);
      }
    } catch (error) {
      console.error(error);
      Alert.alert('Error', 'Hubo un problema al usar la cámara.');
    } finally {
      setUploadingDoc(null);
    }
  };

  /**
   * Valida y normaliza lo escrito en el formulario del vehículo.
   * Devuelve null —y avisa por qué— si falta algo obligatorio.
   *
   * Lo usan LOS DOS caminos (el alta completa y el guardado suelto del chofer
   * ya verificado) para que no puedan exigir cosas distintas ni normalizar la
   * placa de dos maneras. Mismas reglas y mismos textos que antes.
   */
  const validarVehiculo = () => {
    const cleanBrand = vehicleBrand.trim();
    const cleanModel = vehicleModel.trim();
    const cleanColor = vehicleColor.trim();
    // La placa se normaliza ANTES de mirarla: mayúsculas y sin guiones ni
    // espacios, para que `A-123 456` y `a123456` sean la misma placa y no dos.
    const cleanPlate = normalizarPlaca(licensePlate);
    const cleanYear = vehicleYear.trim();
    if (!cleanBrand) {
      Alert.alert('Falta la marca', 'Ingresa la marca de tu vehículo (ej. Toyota, Honda, Suzuki).');
      return null;
    }
    if (!cleanModel) {
      Alert.alert('Falta el modelo', 'Ingresa el modelo (ej. Corolla, Civic, GN125).');
      return null;
    }
    if (!cleanColor) {
      Alert.alert('Falta el color', 'Ingresa el color de tu vehículo (ej. Blanco, Rojo, Negro).');
      return null;
    }
    // ── LA PLACA, DE VERDAD ────────────────────────────────────────────────
    // Aquí había esto, y era TODA la validación:
    //
    //     if (!cleanPlate || cleanPlate.length < 4)
    //
    // Cuatro caracteres cualesquiera. El fundador escribió `A43A548` para
    // probarlo y la app lo guardó en su perfil de chofer (quedó en Firestore
    // con hora: 10-sep-2026 19:11). La placa es lo único que el pasajero puede
    // comprobar con sus ojos antes de montarse; si acepta cualquier cosa, ese
    // chequeo no comprueba nada. Ver utils/placa.ts para el formato y por qué.
    const revision = revisarPlaca(cleanPlate, isMotorcycle);
    if (!revision.ok) {
      Alert.alert(revision.titulo, revision.motivo);
      return null;
    }
    return { cleanBrand, cleanModel, cleanColor, cleanPlate: revision.placa, cleanYear };
  };

  /**
   * GUARDAR SOLO EL VEHÍCULO — el camino del chofer que ya está verificado.
   *
   * Diferencias a propósito con el guardado del alta:
   *  - Se ESPERA la escritura. En el alta es fire-and-forget porque detrás
   *    viene la biometría y no se quiere bloquear la pantalla; aquí el
   *    guardado ES la acción, así que si falla hay que decirlo.
   *  - El almacén local se toca DESPUÉS de que Firestore confirme. Al revés,
   *    su tarjeta "Mi vehículo" mostraría una placa que el pasajero no ve,
   *    porque las ofertas leen el documento del servidor, no el almacén.
   */
  const handleGuardarVehiculo = async () => {
    const v = validarVehiculo();
    if (!v) return;
    if (!userFirebaseUid) {
      Alert.alert(
        'Sesión no detectada',
        'Tu sesión no se detectó. Cierra la app y vuelve a iniciar sesión antes de guardar los datos de tu vehículo.'
      );
      return;
    }
    setGuardandoVehiculo(true);
    setResultadoGuardado(null);
    try {
      await firestoreService.updateUserProfile(userFirebaseUid, {
        vehicleBrand: v.cleanBrand,
        vehicleModel: v.cleanModel,
        vehicleColor: v.cleanColor,
        vehicleYear: v.cleanYear || null,
        licensePlate: v.cleanPlate,
      });
      setVehicleInfo({
        brand: v.cleanBrand,
        model: v.cleanModel,
        color: v.cleanColor,
        year: v.cleanYear || null,
        plate: v.cleanPlate,
      });
      setFaltantesVehiculo([]);
      setResultadoGuardado({
        ok: true,
        texto: `Guardado. El pasajero verá: ${v.cleanColor} ${v.cleanBrand} ${v.cleanModel} · Placa ${v.cleanPlate}.`,
      });
      Alert.alert(
        'Datos de tu vehículo guardados',
        `El pasajero verá: ${v.cleanColor} ${v.cleanBrand} ${v.cleanModel} · Placa ${v.cleanPlate}.`,
        // Al que vino solo a esto se le devuelve a donde estaba; al que está
        // en el alta no se le mueve la pantalla debajo de los pies.
        [{ text: 'Listo', onPress: vinoSoloPorElVehiculo ? () => handleBackToVehiclePicker() : undefined }]
      );
    } catch (e: any) {
      // Sin decir esto, el chofer se va creyendo que quedó registrado y sigue
      // saliendo en la calle como un vehículo sin identificar.
      console.warn('[KYC] guardar vehículo falló:', e?.message);
      setResultadoGuardado({
        ok: false,
        texto: 'No se guardó nada. Revisa tu conexión y vuelve a tocar Guardar.',
      });
      Alert.alert(
        'No pudimos guardar los datos de tu vehículo',
        mensajeDeError(e, 'Nada se guardó todavía. Revisa tu conexión y vuelve a tocar Guardar.')
      );
    } finally {
      setGuardandoVehiculo(false);
    }
  };

  const handleNext = async () => {
    // FIX UBER-STYLE — Para choferes exigimos también los datos del vehículo
    // (marca, modelo, color, placa) antes de pasar a la biométrica. Sin
    // estos datos el pasajero no sabe qué carro esperar y el card queda
    // con "Vehículo del chofer" genérico. Pasajeros no tocan este bloque.
    if (conVehiculoPropio) {
      const v = validarVehiculo();
      if (!v) return;
      // Persistir en store local (avatar y cabecera del RideOfferCard leen
      // de aquí en cada oferta) y fire-and-forget en Firestore.
      setVehicleInfo({
        brand: v.cleanBrand,
        model: v.cleanModel,
        color: v.cleanColor,
        year: v.cleanYear || null,
        plate: v.cleanPlate,
      });
      if (userFirebaseUid) {
        firestoreService
          .updateUserProfile(userFirebaseUid, {
            vehicleBrand: v.cleanBrand,
            vehicleModel: v.cleanModel,
            vehicleColor: v.cleanColor,
            vehicleYear: v.cleanYear || null,
            licensePlate: v.cleanPlate,
          })
          .catch((e) => console.warn('[KYC] persist vehicle info falló:', e?.message));
      }
    }

    // QA brief: feedback granular UNO POR UNO. Antes el mensaje agregaba
    // todos los faltantes en una lista. Ahora avisamos por el PRIMERO
    // faltante en orden, con un texto específico por tipo de documento —
    // así el user sabe exactamente qué tomar a continuación sin leer
    // una lista. El orden refleja el flujo natural del KYC.
    //
    // Cero regresión: la lista `requiredDocs` se mantiene idéntica al
    // contrato previo (Frente + Dorso). El comportamiento que cambia es
    // SOLO el formato del Alert.
    const requiredDocs: string[] = ['Cédula - Frente', 'Cédula - Dorso'];

    const granularMessageByDoc: Record<string, string> = {
      'Cédula - Frente':
        'Falta la foto del FRENTE de la Cédula (lado con tu foto y nombre).',
      'Cédula - Dorso':
        'Falta la foto del DORSO de la Cédula (lado con firma y código de barras).',
      'Licencia de Conducir':
        'Falta la foto de la Licencia de Conducir.',
      'Matrícula del Vehículo':
        'Falta la foto de la Matrícula del Vehículo.',
      'Seguro Vigente':
        'Falta la foto del Seguro Vigente.',
    };

    for (const doc of requiredDocs) {
      if (!uploadedDocs[doc]) {
        Alert.alert(
          'Documento faltante',
          granularMessageByDoc[doc] ?? `Falta: ${doc}`,
          [{ text: 'Entendido' }]
        );
        return;
      }
    }

    // ══════════════════════════════════════════════════════════════════════
    // AL QUE YA ESTÁ VERIFICADO NO SE LE VUELVE A PEDIR LA CARA
    // ══════════════════════════════════════════════════════════════════════
    //
    // Regla del fundador (4-oct-2026): «un usuario que ya es pasajero, como
    // está verificado con su documento, solo tendría que completar lo que le
    // falta para yo validarlo: foto del carro, matrícula, licencia, seguro».
    //
    // Hasta hoy TODO EL MUNDO pasaba por la selfie, y encima el aviso de más
    // abajo le pedía repetir el frente de la cédula desde este teléfono. O
    // sea que un pasajero verificado —al que una persona del equipo YA le
    // comparó la cara con la cédula y le aprobó— tenía que hacer las dos cosas
    // otra vez para poder ser chofer. Es trabajo doble para él y una revisión
    // doble para quien aprueba.
    //
    // POR QUÉ SE PUEDE SALTAR SIN ABRIR NINGÚN HUECO:
    //   · La selfie es OPCIONAL en el servidor: `submitKycForReview` hace
    //     `const haySelfie = selfieUrl !== undefined && selfieUrl !== null`
    //     y sigue adelante sin ella (functions/src/kyc.ts).
    //   · Quien aprueba sigue siendo una persona. Esto no verifica a nadie:
    //     manda la solicitud a la cola igual que antes, solo que con los
    //     papeles del vehículo en vez de con la cara otra vez.
    //   · Y la decisión no se la queda el teléfono: el servidor mira
    //     `users/{uid}.kycCompleted` por su cuenta y marca la solicitud como
    //     `identidadYaVerificada`, que es lo que ve el admin. Si alguien
    //     trucara esta bandera en su app, lo único que conseguiría es mandar
    //     una solicitud SIN selfie que el panel marca como identidad NO
    //     verificada — y ahí se queda.
    if (yaEstaVerificado) {
      setUploadingDoc('__enviando__');
      try {
        const { callSubmitKycForReview } = await import('../services/cloudFunctions');
        await callSubmitKycForReview({ documents: uploadedDocs });
        Alert.alert(
          'Papeles enviados',
          'Tu identidad ya estaba verificada, así que no hace falta repetir la cédula ni la selfie. ' +
            'Mandamos a revisión los papeles de tu vehículo y te avisamos por notificación.'
        );
        router.replace(rutaSegunRol(role) as any);
      } catch (e: any) {
        console.warn('[KYC] no se pudieron enviar los papeles del vehículo:', e?.message);
        Alert.alert(
          'No pudimos enviarlos',
          mensajeDeError(e, 'Revisa tu conexión e inténtalo de nuevo.')
        );
      } finally {
        setUploadingDoc(null);
      }
      return;
    }

    // ─── EL FRENTE REHIDRATADO NO LLEVA SU FOTO ORIGINAL ──────────────────
    // Lo que recuperamos de Firestore es una URL, no la imagen. En móvil el
    // paso de la selfie (kyc-biometrics) exige además el base64 de la foto del
    // frente, que solo existe si se tomó en ESTA sesión. Sin este aviso, el
    // usuario que vuelve tocaría "Continuar", llegaría a la selfie y allí le
    // diría "Foto incompleta" sin explicarle qué repetir: otro callejón.
    // Aquí se le pide UNA foto, no las ocho.
    //
    // Ya solo le toca a quien NO está verificado todavía: el de arriba ni
    // llega aquí, que es justo lo que se arregló.
    if (Platform.OS !== 'web' && !kycDocumentBase64) {
      Alert.alert(
        'Vuelve a tomar el frente de tu cédula',
        'Guardamos los documentos que ya subiste. Solo necesitamos repetir la foto del FRENTE de la cédula desde este teléfono, porque es la cara que una persona de nuestro equipo compara con tu selfie.',
        [{ text: 'Entendido' }]
      );
      return;
    }

    router.push('/kyc-biometrics');
  };

  // Back robusto: NO usar volver(router) porque si el back-stack está
  // partido (signup vía REST hace router.replace), volver(router) puede
  // tirar al index y el guard del _layout lo rebota aquí mismo dejando
  // al user atrapado. Vamos siempre a /kyc-welcome (vehicle picker) que
  // es el único lugar donde puede cambiar el tipo de vehículo o salir
  // a "Cambiar de rol".
  const handleBackToVehiclePicker = () => {
    // EL QUE VINO SOLO A ESCRIBIR SU PLACA NO PUEDE ACABAR EN EL SELECTOR DE
    // VEHÍCULO. /kyc-welcome es un paso del ALTA: al elegir ahí un vehículo se
    // llama a `setDriverRole`, que reescribe rol y tipo de vehículo por Cloud
    // Function. Un chofer verificado que solo venía a completar la marca
    // saldría de ahí con el rol tocado. Vuelve por donde entró.
    if (vinoSoloPorElVehiculo) {
      if (router.canGoBack()) router.back();
      else router.replace('/driver-subscription' as any);
      return;
    }
    // El chofer ya aprobado vuelve por donde entro, nunca al selector de
    // vehiculo del alta. Si no hay historial (llego por un `replace`), a la
    // pantalla de su rol — no a un paso del registro.
    if (yaEstaVerificado) {
      if (router.canGoBack()) router.back();
      else router.replace(rutaSegunRol(role) as any);
      return;
    }
    router.replace('/kyc-welcome');
  };

  // Salida directa a la app desde ESTE paso. Sin ella, quien llegaba hasta
  // aquí y no tenía la cédula a mano solo podía retroceder a la pantalla
  // anterior; ahora sale a su pantalla y lo ya subido queda guardado.
  const handleAhoraNo = () => {
    aplazarVerificacionEnEstaSesion();
    // `entrarALaApp` y no `replace`: desde una pantalla del stack raíz, un
    // replace hacia las pestañas apila una SEGUNDA copia del menú encima de la
    // primera. Ver el comentario del ayudante.
    entrarALaApp(router, rutaSegunRol(role));
  };

  return (
    <ScreenBackground>
      <PantallaSegura style={styles.safeArea}>
        {/* Header sticky FUERA del ScrollView. Antes el botón vivía dentro
            del scroll → se ocultaba al bajar y el user creía estar atrapado.
            Y para el PASAJERO era una flecha suelta, sin una palabra al lado,
            dentro del scroll: la misma trampa. Ahora los dos roles tienen la
            misma barra fija, con texto, y con la salida a la app al lado. */}
        <View style={[styles.stickyHeader, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
          <TouchableOpacity
            onPress={handleBackToVehiclePicker}
            style={styles.stickyBackBtn}
            activeOpacity={0.7}
            accessibilityRole="button"
          >
            <Ionicons name="arrow-back" size={20} color={Colors.primary} style={{ marginRight: 6 }} />
            <Text style={styles.stickyBackText}>
              {isPassenger || sinVehiculoPropio || vinoSoloPorElVehiculo || yaEstaVerificado ? 'Volver' : 'Cambiar vehículo'}
            </Text>
          </TouchableOpacity>
          {/* «Ahora no» aplaza la verificacion. A quien YA esta verificado no
              le queda nada que aplazar: era un boton que prometia una accion
              inexistente, al lado de otro que ofrecia rehacer el vehiculo. Los
              dos juntos son lo que el fundador leyo como «eso no puede ir ahi
              porque ya esta puesta en el perfil». */}
          {!KYC_WALL_ENFORCED && !yaEstaVerificado && (
            <TouchableOpacity
              onPress={handleAhoraNo}
              style={styles.stickyAhoraNoBtn}
              activeOpacity={0.7}
              accessibilityRole="button"
            >
              <Text style={styles.stickyAhoraNoText}>Ahora no</Text>
              <Ionicons name="arrow-forward" size={16} color={Colors.primary} style={{ marginLeft: 4 }} />
            </TouchableOpacity>
          )}
        </View>
        <ScrollView ref={scrollRef} contentContainerStyle={styles.container}>
        {rehidratando && (
          <Text style={styles.avisoRehidratando}>Buscando los documentos que ya subiste…</Text>
        )}
        <View style={styles.header}>
          <Text style={styles.title}>Documentos Requeridos</Text>
          {/* DOS PROMESAS QUE LA APP NO CUMPLE, CORREGIDAS:
              · "escudo verde de perfil biométrico" — no hay perfil biométrico.
                privacy.tsx punto 2.c: la selfie y la cédula "las compara a
                mano una persona autorizada de Go Safely… no generamos ni
                almacenamos plantillas ni vectores faciales".
              · "usando nuestra IA segura" — no hay ninguna IA. La revisión la
                hace una persona: `reviewKycSubmission` (functions/src/kyc.ts)
                es una llamada de admin (`assertAdminWithMfa`) que aprueba o
                rechaza a mano, y terms.tsx punto 4 dice que "no usamos…
                ningún sistema automático". */}
          <Text style={styles.subtitle}>
            {isPassenger
              ? 'Para validar tu cuenta y asignarte tu escudo verde de perfil verificado, necesitamos verificar tu documento oficial.'
              : sinVehiculoPropio
              ? 'Necesitamos tu cédula y tu licencia de conducir. Los papeles del carro los pone el dueño: a ti no te pedimos nada de un vehículo que no es tuyo.'
              : `Para tu ${vehicle === 'CAR' ? 'Carro' : vehicle === 'MOTORCYCLE' ? 'Motor' : 'Minibus'}, necesitamos revisar los siguientes documentos. Los revisa una persona autorizada de Go Safely.`
            }
          </Text>
        </View>

        <View style={{ backgroundColor: 'rgba(255, 149, 0, 0.1)', padding: 16, borderRadius: 12, marginBottom: 16, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: 'rgba(255, 149, 0, 0.3)' }}>
          <Ionicons name="shield-checkmark" size={28} color="#FF9500" style={{ marginRight: 12 }} />
          <Text style={{ flex: 1, fontSize: 13, color: '#D07B00', lineHeight: 18 }}>
            <Text style={{ fontWeight: 'bold' }}>Seguridad Anti-Fraude Activa:</Text> Para garantizar la máxima seguridad y evitar suplantación de identidad (plagio), <Text style={{ fontWeight: 'bold', textDecorationLine: 'underline' }}>todas las fotos deben ser tomadas en vivo en este momento</Text> con la cámara de su dispositivo. No se permiten fotos subidas de la galería.
          </Text>
        </View>

        {conVehiculoPropio && vehicle === 'CAR' && (
          <View style={{ backgroundColor: 'rgba(33, 150, 243, 0.08)', padding: 16, borderRadius: 12, marginBottom: 16, borderWidth: 1, borderColor: 'rgba(33, 150, 243, 0.2)' }}>
            <Text style={{ color: '#0D47A1', fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>
              📋 Requisitos Oficiales del Vehículo
            </Text>
            {/* La última viñeta decía "Verificación IA … se validarán
                automáticamente". No existe ninguna IA: quien aprueba es un
                admin desde `reviewKycSubmission` (functions/src/kyc.ts), y
                terms.tsx punto 4 promete que "no usamos reconocimiento facial,
                prueba de vida ni ningún sistema automático". */}
            <Text style={{ fontSize: 13, color: '#1565C0', lineHeight: 18 }}>
              • <Text style={{ fontWeight: 'bold' }}>Antigüedad:</Text> Modelos del año 2010 en adelante. Vehículos más viejos NO se aceptan por seguridad estructural y disponibilidad de piezas.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Equipamiento Obligatorio:</Text> Aire acondicionado funcional, todas las puertas abren/cierran, cinturones en buen estado, faros y luces de cola funcionando.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Pintura y Carrocería:</Text> Sin daños grandes ni óxido visible. Se permiten rayones menores.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Interior:</Text> Asientos sin roturas grandes ni manchas profundas. Sin olores fuertes. Tablero íntegro.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Revisión de las fotos:</Text> La foto frontal en vivo y la del interior las revisa una persona de nuestro equipo antes de autorizar el ingreso del vehículo.
            </Text>
          </View>
        )}

        {conVehiculoPropio && vehicle === 'SCHOOL_BUS' && (
          <View style={{ backgroundColor: 'rgba(255, 59, 48, 0.08)', padding: 16, borderRadius: 12, marginBottom: 16, borderWidth: 1, borderColor: 'rgba(255, 59, 48, 0.2)' }}>
            <Text style={{ color: '#D32F2F', fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>
              🏫 Requisitos Oficiales - Transporte Escolar (Premium / Alta Seguridad)
            </Text>
            <Text style={{ fontSize: 13, color: '#C62828', lineHeight: 18 }}>
              • <Text style={{ fontWeight: 'bold' }}>Antigüedad Máxima:</Text> Vehículo de 5 a 8 años máximo (Modelos del 2018 al 2021 en adelante).{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Tipología Recomendada:</Text> SUV de tres filas (jeepetas) o minivans, debido a su gran resistencia e índices de seguridad estructural en colisiones.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Seguridad de Pasajeros:</Text> Cinturones de seguridad funcionales en todos los asientos traseros (Requisito Vital).{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Seguros Infantiles:</Text> Seguros de puertas traseras para niños (Child locks) activados obligatoriamente.{"\n"}
              • <Text style={{ fontWeight: 'bold' }}>Climatización:</Text> El aire acondicionado (A/C) debe funcionar en óptimas condiciones.
            </Text>
          </View>
        )}

        <View style={styles.card}>
          <Text style={styles.sectionHeader}>
            {/* "Tu identidad" y no "del Pasajero": por aquí pasan también el
                cliente de comida y el que busca vehículo. */}
            {isPassenger ? 'Tu identidad' : 'Identidad del Chofer'}
          </Text>
          <DocumentUploadRow
            title="Cédula - Frente"
            subtitle="Lado con la foto y el nombre"
            isUploaded={!!uploadedDocs['Cédula - Frente']}
            isUploading={uploadingDoc === 'Cédula - Frente'}
            onPress={() => handleUpload('Cédula - Frente')}
          />
          <DocumentUploadRow
            title="Cédula - Dorso"
            subtitle="Lado con la firma y el código de barras"
            isUploaded={!!uploadedDocs['Cédula - Dorso']}
            isUploading={uploadingDoc === 'Cédula - Dorso'}
            onPress={() => handleUpload('Cédula - Dorso')}
          />
          
          {!isPassenger && (
            <>
              <DocumentUploadRow 
                title="Licencia de Conducir" 
                subtitle="Frente y Dorso" 
                isUploaded={!!uploadedDocs['Licencia de Conducir']}
                isUploading={uploadingDoc === 'Licencia de Conducir'}
                onPress={() => handleUpload('Licencia de Conducir')}
              />
              {/* Papel de Buena Conducta — SOLO RUTA ESCOLAR (27-sep-2026).
                  Al chofer y al repartidor ya no se les pide: con cédula,
                  licencia, matrícula, seguro y fotos del vehículo basta (ver el
                  comentario largo del go-online en map.tsx). Quien transporta
                  ESTUDIANTES sí lo necesita, y llega a esta pantalla mandado
                  por school-driver-setup.tsx, que es quien lo exige.
                  La fila también aparece si YA hay uno subido: si no, el papel
                  que alguien mandó desaparecería de su pantalla sin explicación
                  (el documento no se borra, solo se deja de pedir). */}
              {(esChoferEscolar || !!uploadedDocs['Papel de Buena Conducta']) && (
                <DocumentUploadRow
                  title="Papel de Buena Conducta"
                  subtitle={
                    esChoferEscolar
                      ? 'Obligatorio para transportar estudiantes: de la Procuraduría, sin antecedentes y con menos de 30 días.'
                      : 'Ya no se pide para manejar ni repartir. Queda a la vista porque tú lo subiste; puedes reemplazarlo si quieres.'
                  }
                  required={esChoferEscolar}
                  isUploaded={!!uploadedDocs['Papel de Buena Conducta']}
                  isUploading={uploadingDoc === 'Papel de Buena Conducta'}
                  onPress={() => handleUpload('Papel de Buena Conducta')}
                />
              )}
            </>
          )}
        </View>

        {conVehiculoPropio && (
          <View
            style={styles.card}
            onLayout={(e) => {
              yDelFormularioVehiculo.current = e.nativeEvent.layout.y;
              desplazarAlVehiculo();
            }}
          >
            <Text style={styles.sectionHeader}>Identificación del Vehículo</Text>

            {/* AVISO DE LO QUE FALTA — solo para el chofer ya verificado.
                Dice EXACTAMENTE qué campos faltan, no "completa tu perfil".
                Se comprobó en producción que se puede estar verificado y en
                línea sin ninguno de estos datos, y hasta hoy nadie se lo
                decía al chofer: se enteraba porque el pasajero se quejaba de
                que no sabía qué carro esperar. */}
            {mostrarGuardadoDeVehiculo && faltantesVehiculo.length > 0 && (
              <View style={styles.avisoFaltaVehiculo}>
                <Ionicons name="alert-circle" size={22} color="#B26A00" style={{ marginRight: 10 }} />
                <View style={{ flex: 1 }}>
                  {/* "No está registrado" SOLO si no hay ninguno de los
                      cuatro. Si falta uno, decir lo otro sería exagerar. */}
                  <Text style={styles.avisoFaltaTitulo}>
                    {faltantesVehiculo.length === 4
                      ? `Tu ${isMotorcycle ? 'motor' : 'vehículo'} no está registrado`
                      : `Faltan datos de tu ${isMotorcycle ? 'motor' : 'vehículo'}`}
                  </Text>
                  <Text style={styles.avisoFaltaTexto}>
                    Falta {faltantesVehiculo.join(', ').replace(/, ([^,]*)$/, ' y $1')}. Sin
                    esto el pasajero no sabe qué {isMotorcycle ? 'motor' : 'carro'} esperar ni
                    puede comprobar la placa al montarse. Complétalo aquí abajo y toca Guardar.
                  </Text>
                </View>
              </View>
            )}

            <Text style={{ fontSize: 12, color: Colors.textSecondary, marginBottom: 12 }}>
              El pasajero verá estos datos para identificar tu {isMotorcycle ? 'motor' : 'vehículo'} al llegar. Rellena con datos reales — se verifican contra la matrícula.
            </Text>

            <View style={styles.formRow}>
              <View style={{ flex: 2 }}>
                <Text style={styles.formLabel}>Marca *</Text>
                <TextInput
                  style={styles.formInput}
                  value={vehicleBrand}
                  onChangeText={setVehicleBrandLocal}
                  placeholder={isMotorcycle ? 'Suzuki' : 'Toyota'}
                  placeholderTextColor={Colors.textSecondary}
                  autoCapitalize="words"
                />
              </View>
              <View style={{ flex: 2 }}>
                <Text style={styles.formLabel}>Modelo *</Text>
                <TextInput
                  style={styles.formInput}
                  value={vehicleModel}
                  onChangeText={setVehicleModelLocal}
                  placeholder={isMotorcycle ? 'GN125' : 'Corolla'}
                  placeholderTextColor={Colors.textSecondary}
                  autoCapitalize="words"
                />
              </View>
            </View>

            <View style={styles.formRow}>
              <View style={{ flex: 2 }}>
                <Text style={styles.formLabel}>Color *</Text>
                <TextInput
                  style={styles.formInput}
                  value={vehicleColor}
                  onChangeText={setVehicleColorLocal}
                  placeholder="Blanco"
                  placeholderTextColor={Colors.textSecondary}
                  autoCapitalize="words"
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.formLabel}>Año</Text>
                <TextInput
                  style={styles.formInput}
                  value={vehicleYear}
                  onChangeText={setVehicleYearLocal}
                  placeholder="2020"
                  placeholderTextColor={Colors.textSecondary}
                  keyboardType="number-pad"
                  maxLength={4}
                />
              </View>
            </View>

            <Text style={styles.formLabel}>Placa (matrícula) *</Text>
            <TextInput
              style={[styles.formInput, { letterSpacing: 2, fontWeight: 'bold', fontSize: 16 }]}
              value={licensePlate}
              /* Se limpia MIENTRAS escribe, no al guardar: así no puede teclear
                 guiones ni espacios y lo que ve en pantalla es exactamente lo
                 que se va a guardar y lo que va a ver el pasajero. */
              onChangeText={(t) => setLicensePlateLocal(normalizarPlaca(t))}
              placeholder={isMotorcycle ? 'K12345' : 'A123456'}
              placeholderTextColor={Colors.textSecondary}
              autoCapitalize="characters"
              /* 9 = 2 letras + 7 dígitos, el máximo que admite el formato.
                 Estaba en 12, que daba sitio a escribir cosas que no son placas. */
              maxLength={9}
            />
            {/* El chofer tiene que saber qué se le pide ANTES de equivocarse,
                no después de un aviso. */}
            <Text style={styles.ayudaCampo}>{ayudaPlaca(isMotorcycle)}</Text>

            {/* GUARDAR SIN REPETIR LA VERIFICACIÓN.
                El botón del final de la pantalla sigue hacia la selfie, y
                al chofer ya verificado esa puerta le está cerrada (en móvil
                pide repetir la foto del frente de la cédula). Sin este botón
                no tenía NINGUNA forma de guardar marca, modelo, color y
                placa después de haberse verificado. */}
            {mostrarGuardadoDeVehiculo && (
              <>
                <TouchableOpacity
                  style={[styles.guardarVehiculoBtn, guardandoVehiculo && { opacity: 0.6 }]}
                  onPress={handleGuardarVehiculo}
                  disabled={guardandoVehiculo}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                >
                  {guardandoVehiculo ? (
                    <ActivityIndicator size="small" color={Colors.white} />
                  ) : (
                    <>
                      <Ionicons name="save" size={18} color={Colors.white} style={{ marginRight: 8 }} />
                      <Text style={styles.guardarVehiculoTexto}>Guardar datos de mi vehículo</Text>
                    </>
                  )}
                </TouchableOpacity>
                {resultadoGuardado && (
                  <Text
                    style={[
                      styles.guardarVehiculoResultado,
                      { color: resultadoGuardado.ok ? '#1B5E20' : Colors.error },
                    ]}
                  >
                    {resultadoGuardado.texto}
                  </Text>
                )}
                <Text style={styles.guardarVehiculoNota}>
                  {faltantesVehiculo.length === 0
                    ? 'Ya están guardados. Si cambiaste de vehículo, corrígelos aquí y vuelve a guardar.'
                    : 'Se guardan aquí mismo, sin repetir la verificación ni las fotos.'}
                </Text>
              </>
            )}
          </View>
        )}

        {conVehiculoPropio && (
          <View style={styles.card}>
            <Text style={styles.sectionHeader}>Documentos del Vehículo</Text>
            <DocumentUploadRow
              title="Matrícula del Vehículo"
              isUploaded={!!uploadedDocs['Matrícula del Vehículo']}
              isUploading={uploadingDoc === 'Matrícula del Vehículo'}
              onPress={() => handleUpload('Matrícula del Vehículo')}
            />
            <DocumentUploadRow 
              title="Seguro Vigente" 
              isUploaded={!!uploadedDocs['Seguro Vigente']}
              isUploading={uploadingDoc === 'Seguro Vigente'}
              onPress={() => handleUpload('Seguro Vigente')}
            />
            
            <Text style={[styles.sectionHeader, { marginTop: 16 }]}>Fotos del Vehículo (En Vivo)</Text>
            <Text style={{ fontSize: 12, color: Colors.textSecondary, marginBottom: 12 }}>
              Tome 4 fotos claras donde se aprecie el estado físico actual del {isMotorcycle ? 'motor' : 'vehículo'}.
            </Text>
            
            {isMotorcycle ? (
              <>
                <DocumentUploadRow 
                  title="1. Lateral Derecho Completo" 
                  subtitle="El motor debe verse de forma clara y completa" 
                  isUploaded={!!uploadedDocs['1. Lateral Derecho Completo']}
                  isUploading={uploadingDoc === '1. Lateral Derecho Completo'}
                  onPress={() => handleUpload('1. Lateral Derecho Completo')}
                />
                <DocumentUploadRow
                  title="2. Lado de la Placa"
                  subtitle="La placa perfectamente legible"
                  isUploaded={!!uploadedDocs['2. Lado de la Placa']}
                  isUploading={uploadingDoc === '2. Lado de la Placa'}
                  onPress={() => handleUpload('2. Lado de la Placa')}
                />
                <DocumentUploadRow
                  title="Casco rotulado con tu placa"
                  subtitle="Foto de tu casco con el número de placa de tu moto rotulado (visible y legible). Obligatorio para motoristas. El 2do casco (del pasajero) es opcional."
                  isUploaded={!!uploadedDocs['Casco rotulado con tu placa']}
                  isUploading={uploadingDoc === 'Casco rotulado con tu placa'}
                  onPress={() => handleUpload('Casco rotulado con tu placa')}
                />
                <DocumentUploadRow 
                  title="3. Modelo del Chasis" 
                  subtitle="Código/Numeración del chasis para revisión de seguridad" 
                  isUploaded={!!uploadedDocs['3. Modelo del Chasis']}
                  isUploading={uploadingDoc === '3. Modelo del Chasis'}
                  onPress={() => handleUpload('3. Modelo del Chasis')}
                />
              </>
            ) : (
              <>
                <DocumentUploadRow 
                  title="1. Frente del Vehículo" 
                  subtitle="Vista frontal completa" 
                  isUploaded={!!uploadedDocs['1. Frente del Vehículo']}
                  isUploading={uploadingDoc === '1. Frente del Vehículo'}
                  onPress={() => handleUpload('1. Frente del Vehículo')}
                />
                <DocumentUploadRow 
                  title="2. Lateral Derecho" 
                  subtitle="Costado derecho completo" 
                  isUploaded={!!uploadedDocs['2. Lateral Derecho']}
                  isUploading={uploadingDoc === '2. Lateral Derecho'}
                  onPress={() => handleUpload('2. Lateral Derecho')}
                />
                <DocumentUploadRow 
                  title="3. Lateral Izquierdo" 
                  subtitle="Costado izquierdo completo" 
                  isUploaded={!!uploadedDocs['3. Lateral Izquierdo']}
                  isUploading={uploadingDoc === '3. Lateral Izquierdo'}
                  onPress={() => handleUpload('3. Lateral Izquierdo')}
                />
                <DocumentUploadRow
                  title="4. Parte Trasera"
                  subtitle="La placa debe ser perfectamente legible"
                  isUploaded={!!uploadedDocs['4. Parte Trasera']}
                  isUploading={uploadingDoc === '4. Parte Trasera'}
                  onPress={() => handleUpload('4. Parte Trasera')}
                />
                <DocumentUploadRow
                  title="5. Interior del Vehículo"
                  subtitle="Foto desde el asiento del chofer mostrando tablero, asientos delanteros y traseros."
                  isUploaded={!!uploadedDocs['5. Interior del Vehículo']}
                  isUploading={uploadingDoc === '5. Interior del Vehículo'}
                  onPress={() => handleUpload('5. Interior del Vehículo')}
                />
              </>
            )}

            {vehicle === 'SCHOOL_BUS' && (
              <>
                <DocumentUploadRow 
                  title="5. Foto de Asientos y Cinturones" 
                  subtitle="Todos los cinturones traseros funcionales" 
                  isUploaded={!!uploadedDocs['5. Foto de Asientos y Cinturones']}
                  isUploading={uploadingDoc === '5. Foto de Asientos y Cinturones'}
                  onPress={() => handleUpload('5. Foto de Asientos y Cinturones')}
                />
                <DocumentUploadRow 
                  title="6. Seguro de Puertas Niños" 
                  subtitle="Foto del indicador (Child Locks) activo" 
                  isUploaded={!!uploadedDocs['6. Seguro de Puertas Niños']}
                  isUploading={uploadingDoc === '6. Seguro de Puertas Niños'}
                  onPress={() => handleUpload('6. Seguro de Puertas Niños')}
                />
              </>
            )}

            {isMotorcycle && (
              <View style={{ marginTop: 12, padding: 12, backgroundColor: 'rgba(255, 59, 48, 0.05)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255, 59, 48, 0.2)' }}>
                <Text style={{ color: Colors.error, fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>
                  ⚠️ Requisito Estricto de Seguridad
                </Text>
                {/* Decía "El sistema comparará las fotos…": otra vez una
                    comparación automática que no existe. La revisión es la
                    misma de siempre — `reviewKycSubmission`
                    (functions/src/kyc.ts), un admin aprobando o rechazando a
                    mano — y terms.tsx punto 4 dice que no hay sistema
                    automático de ninguna clase. */}
                <Text style={{ color: Colors.text, fontSize: 13, lineHeight: 18 }}>
                  Nuestro equipo comparará las fotos subidas con el <Text style={{fontWeight: 'bold'}}>modelo de chasis original</Text> y la <Text style={{fontWeight: 'bold'}}>matrícula</Text>. Las discrepancias resultarán en el rechazo del perfil.
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Decía "Tus documentos y biometría". No se recoge ningún dato
            biométrico: privacy.tsx punto 2.h aclara que lo único biométrico de
            la app es el bloqueo del propio teléfono (Face ID/huella), que
            resuelve el sistema operativo y del que Go Safely "recibe un sí o
            un no". Lo que sube por aquí son documentos y una selfie. */}
        <View style={styles.infoBox}>
          <Text style={{fontSize: 20, marginRight: 12}}>🔒</Text>
          <Text style={styles.infoText}>
            Tus documentos y tu selfie viajan por conexión segura (TLS) y se guardan cifrados en reposo. Tu selfie, una vez aprobada, pasa a ser tu foto de perfil{conVehiculoPropio ? ', y una foto de tu vehículo (la parte trasera del carro, o el lado de la placa del motor; si falta, un lateral o el frente) se le muestra, junto con tu placa, al pasajero al que le ofreces un viaje' : ''}.
          </Text>
        </View>
      </ScrollView>

      {/* El botón decía "Continuar a Biometría". El paso siguiente
          (kyc-biometrics.tsx) se titula en pantalla "Selfie de verificación" y
          eso es exactamente lo que pide. Llamarlo biometría contradice
          terms.tsx punto 4 y privacy.tsx punto 2.c, y desalinea el botón de la
          pantalla a la que lleva. */}
      <View style={styles.footer}>
        <Button
          title={isPassenger ? "Validar y Continuar a la Selfie" : "Validar Documentos y Continuar a la Selfie"}
          onPress={handleNext}
        />
      </View>
    </PantallaSegura>
  </ScreenBackground>
  );
}

interface DocumentUploadRowProps {
  title: string;
  subtitle?: string;
  required?: boolean;
  isUploaded: boolean;
  isUploading: boolean;
  onPress: () => void;
}

const DocumentUploadRow = ({ title, subtitle, required = true, isUploaded, isUploading, onPress }: DocumentUploadRowProps) => (
  <View style={styles.docRow}>
    <View style={styles.docInfo}>
      <Text style={styles.docTitle}>{title} {required && <Text style={styles.required}>*</Text>}</Text>
      {subtitle && <Text style={styles.docSubtitle}>{subtitle}</Text>}
    </View>
    <TouchableOpacity 
      style={[
        styles.uploadButton, 
        isUploaded && { backgroundColor: '#4CAF50' },
        isUploading && { backgroundColor: '#FFA000' }
      ]}
      onPress={onPress}
      disabled={isUploading}
    >
      {isUploading ? (
        <ActivityIndicator size="small" color="#FFF" />
      ) : isUploaded ? (
        <>
          <Ionicons name="checkmark-circle" size={16} color={Colors.white} style={{ marginRight: 4 }} />
          <Text style={styles.uploadText}>Cargado</Text>
        </>
      ) : (
        <>
          <Ionicons name="camera" size={16} color={Colors.white} style={{ marginRight: 4 }} />
          <Text style={styles.uploadText}>Cámara</Text>
        </>
      )}
    </TouchableOpacity>
  </View>
);

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flexGrow: 1,
    padding: SIZES.padding,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    paddingBottom: 100,
  },
  header: {
    marginTop: Platform.OS === 'ios' ? 10 : 20,
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    lineHeight: 22,
  },
  card: {
    backgroundColor: Colors.white,
    borderRadius: SIZES.radius,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  sectionHeader: {
    fontSize: SIZES.medium,
    fontWeight: 'bold',
    color: Colors.primary,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  formRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 4,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.textSecondary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  /* Ayuda debajo de un campo. El texto lo da utils/placa.ts para que las
     pantallas que piden placa no se contradigan entre ellas. */
  ayudaCampo: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 6,
    marginBottom: 4,
  },
  formInput: {
    backgroundColor: '#F5F7FA',
    borderWidth: 1,
    borderColor: Colors.border,
    borderRadius: 12,
    paddingHorizontal: 14,
    height: 46,
    fontSize: 14,
    color: Colors.text,
    marginBottom: 12,
  },
  // Aviso de datos del vehículo que faltan (chofer ya verificado).
  avisoFaltaVehiculo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(255, 149, 0, 0.10)',
    borderWidth: 1,
    borderColor: 'rgba(255, 149, 0, 0.35)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 14,
  },
  avisoFaltaTitulo: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#B26A00',
    marginBottom: 4,
  },
  avisoFaltaTexto: {
    fontSize: 12.5,
    color: '#8A5200',
    lineHeight: 18,
  },
  guardarVehiculoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primary,
    borderRadius: 12,
    height: 48,
    marginTop: 4,
  },
  guardarVehiculoTexto: {
    color: Colors.white,
    fontWeight: 'bold',
    fontSize: 15,
  },
  guardarVehiculoResultado: {
    fontSize: 13,
    fontWeight: '700',
    marginTop: 10,
    lineHeight: 18,
  },
  guardarVehiculoNota: {
    fontSize: 11.5,
    color: Colors.textSecondary,
    marginTop: 8,
    lineHeight: 16,
  },
  docRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  docInfo: {
    flex: 1,
    paddingRight: 12,
  },
  docTitle: {
    fontSize: SIZES.font,
    fontWeight: '600',
    color: Colors.text,
  },
  required: {
    color: Colors.error,
  },
  docSubtitle: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 2,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.accent,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    minWidth: 105,
    justifyContent: 'center',
  },
  uploadText: {
    color: Colors.white,
    fontWeight: 'bold',
    fontSize: 12,
  },
  infoBox: {
    flexDirection: 'row',
    backgroundColor: '#E3F2FD',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: '#1565C0',
    lineHeight: 18,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: SIZES.padding,
    backgroundColor: Colors.background,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  backButton: {
    padding: 8,
    alignSelf: 'flex-start',
    marginBottom: 10,
    marginTop: Platform.OS === 'ios' ? 10 : 20,
  },
  stickyHeader: {
    paddingHorizontal: SIZES.padding,
    // `PantallaSegura` ya aparta la barra de estado: este relleno vuelve a ser
    // solo aire, no un parche para un margen que no se estaba midiendo.
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: Colors.background,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  stickyBackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  stickyBackText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  stickyAhoraNoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: Colors.primary,
  },
  stickyAhoraNoText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  avisoRehidratando: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 8,
    marginBottom: 4,
  },
});
